import React, { Component } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import API from "../utils/api";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseDateOnly = (value) => {
    if (!value) return null;

    const [year, month, day] = String(value).split("-").map(Number);
    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
};

const formatDateOnly = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getResolvedRangeDayCount = (from, to) => {
    const today = parseDateOnly(formatDateOnly(new Date()));
    let startDate = parseDateOnly(from) || today;
    let endDate = parseDateOnly(to) || today;

    if (startDate > endDate) {
        const temp = startDate;
        startDate = endDate;
        endDate = temp;
    }

    return Math.floor((endDate.getTime() - startDate.getTime()) / MS_PER_DAY) + 1;
};

const formatDisplayDate = (value) => {
    const date = parseDateOnly(value);
    if (!date) return value;

    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

class AdminLeadsSummary extends Component {
    state = {
        postedCount: 0,
        attemptedCount: 0,
        avgBuyCount: 0,
        loading: true,
        from: "",
        to: "",
        source: [],
        medicine_type: [],
        service_for: [],
        service_type: "",
        selling_purpose: "",
        location: [],
        user_id: "",
        team_id: "",
        buy_percentage_operator: "",
        buy_percentage_value: "",
        users: [],
        teams: [],
        sourceOptions: [],
        campaignOptions: [],
        medicineTypeOptions: [],
        serviceForOptions: [],
        serviceTypeOptions: [],
        sellingPurposeOptions: [],
        executiveSummary: [],
        perDayPosting: [],
        showPerDayPosting: false,
    };

    componentDidMount() {
        this.loadFilters();
        this.fetchSummary();
    }

    loadFilters = async () => {
        try {
            const [usersRes, constRes, teamsRes] = await Promise.all([
                API.get("/admin/users"),
                API.get("/const-data"),
                API.get("/teams"),
            ]);
            const constData = constRes.data.data || constRes.data || {};
            const usersData = usersRes.data.data || usersRes.data;
            const users = Array.isArray(usersData) ? usersData : [];
            const teamsData = teamsRes.data.data || teamsRes.data;
            const teams = Array.isArray(teamsData) ? teamsData : [];
            this.setState({
                users,
                teams,
                sourceOptions: constData.source || [],
                medicineTypeOptions: constData.medicine_type || [],
                serviceForOptions: constData.service_for || [],
                serviceTypeOptions: constData.enquiry_type || [],
                sellingPurposeOptions: constData.selling_purpose || [],
            });
        } catch (e) {
            console.error("Failed to load filter options", e);
        }
    };

    loadCampaignOptions = async (source) => {
        try {
            if (!source || source.length !== 1) {
                this.setState({ campaignOptions: [], campaign: "" });
                return;
            }
            const res = await API.get("/campaigns?source=" + encodeURIComponent(source[0].value));
            const data = res.data.data || res.data;
            this.setState({ campaignOptions: data || [] });
        } catch (e) {
            console.error("Failed to load campaign options", e);
        }
    };

    loadLocations = async (inputValue) => {
        if (!inputValue || inputValue.length < 2) return [];

        try {
            const res = await API.get("/cities", { params: { q: inputValue } });
            const cities = res.data.data || res.data || [];

            return cities.map((city) => ({
                value: `${city.city}, ${city.state}`,
                label: `${city.city}, ${city.state}`,
                id: city.id,
            }));
        } catch (e) {
            return [];
        }
    };

    fetchSummary = async () => {
        this.setState({ loading: true });
        try {
            const {
                from,
                to,
                source,
                medicine_type,
                service_for,
                service_type,
                selling_purpose,
                location,
                user_id,
                team_id,
                campaign,
                buy_percentage_operator,
                buy_percentage_value,
            } = this.state;
            const params = {};
            if (from) params.from = from;
            if (to) params.to = to;
            if (source && source.length > 0) {
                params.source = source.map((item) => item.value).join(",");
            }
            if (medicine_type && medicine_type.length > 0) {
                params.medicine_type = medicine_type.map(m => m.value).join(",");
            }
            if (service_for && service_for.length > 0) {
                params.service_for = service_for.map(s => s.value).join(",");
            }
            if (service_type) params.service_type = service_type;
            if (selling_purpose) params.selling_purpose = selling_purpose;
            if (location && location.length > 0) {
                params.location = JSON.stringify(location.map((item) => item.value));
            }
            if (user_id) params.user_id = user_id;
            if (team_id) params.team_id = team_id;
            if (campaign) params.campaign = campaign;
            if (buy_percentage_operator && buy_percentage_value !== "") {
                params.buy_percentage_operator = buy_percentage_operator;
                params.buy_percentage_value = buy_percentage_value;
            }

            // Single API call to get summary
            const res = await API.get("/admin/summary", { params });
            const data = res.data.data || res.data;

            this.setState({
                postedCount: data.postedCount || 0,
                attemptedCount: data.attemptedCount || 0,
                avgBuyCount: data.avgBuyCount || 0,
                executiveSummary: data.executiveSummary || [],
                perDayPosting: data.perDayPosting || [],
                loading: false,
            });
        } catch (e) {
            console.error("Failed to fetch summary", e);
            toast.error("Failed to load summary");
            this.setState({ loading: false });
        }
    };

    handleSearch = (e) => {
        e.preventDefault();
        this.fetchSummary();
    };

    handleReset = () => {
        this.setState(
            {
                from: "",
                to: "",
                source: [],
                medicine_type: [],
                service_for: [],
                service_type: "",
                selling_purpose: "",
                location: [],
                user_id: "",
                team_id: "",
                campaign: "",
                buy_percentage_operator: "",
                buy_percentage_value: "",
            },
            this.fetchSummary
        );
    };

    navigateToReport = (type, selectedUserId = "") => {
        const {
            from,
            to,
            source,
            medicine_type,
            service_for,
            service_type,
            selling_purpose,
            location,
            user_id,
            campaign,
        } = this.state;
        const effectiveUserId = selectedUserId || user_id;
        const filters = new URLSearchParams();
        if (from) filters.append("from", from);
        if (to) filters.append("to", to);
        if (source && source.length > 0) {
            filters.append("source", source.map((item) => item.value).join(","));
        }
        if (medicine_type && medicine_type.length > 0) {
            filters.append("medicine_type", medicine_type.map(m => m.value).join(","));
        }
        if (service_for && service_for.length > 0) {
            filters.append("service_for", service_for.map(s => s.value).join(","));
        }
        if (service_type) filters.append("service_type", service_type);
        if (selling_purpose) filters.append("selling_purpose", selling_purpose);
        if (location && location.length > 0) {
            filters.append("location", JSON.stringify(location.map((item) => item.value)));
        }
        if (effectiveUserId) filters.append("user_id", effectiveUserId);
        if (campaign) filters.append("campaign", campaign);
        if (this.state.team_id) filters.append("team_id", this.state.team_id);

        const path = type === "posted" ? "/admin/report/posted" : "/admin/report/attempted";
        this.props.history.push(`${path}?${filters.toString()}`);
    };

    render() {
        const {
            postedCount, attemptedCount, avgBuyCount, loading, from, to, source,
            medicine_type, service_for, service_type, selling_purpose, location, user_id, team_id, campaign,
            buy_percentage_operator, buy_percentage_value,
            users, teams, sourceOptions, campaignOptions, medicineTypeOptions, serviceForOptions, serviceTypeOptions, sellingPurposeOptions,
            executiveSummary, perDayPosting, showPerDayPosting,
        } = this.state;
        const isMultiDayRange = getResolvedRangeDayCount(from, to) > 1;
        const totalPostedTarget = executiveSummary.reduce(
            (sum, exec) => sum + (isMultiDayRange ? Number(exec.total_posted_target) || 0 : Number(exec.posted_target) || 0),
            0
        );
        const totalAttemptedTarget = executiveSummary.reduce(
            (sum, exec) => sum + (isMultiDayRange ? Number(exec.total_attempted_target) || 0 : Number(exec.attempted_target) || 0),
            0
        );

        return (
            <div>
                <h2 className="page-header">
                    <i className="fas fa-chart-pie"></i> Leads Summary
                </h2>

                {/* Filters */}
                <form onSubmit={this.handleSearch} className="well" style={{ marginBottom: 20, padding: 15 }}>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>From</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={from}
                                    onChange={(e) => this.setState({ from: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>To</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={to}
                                    onChange={(e) => this.setState({ to: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Source</label>
                                <Select
                                    isMulti
                                    isClearable
                                    isSearchable
                                    options={sourceOptions.map((s) => ({ label: s, value: s }))}
                                    value={source}
                                    onChange={(selected) => {
                                        this.setState({ source: selected || [], campaign: "" });
                                        this.loadCampaignOptions(selected || []);
                                    }}
                                    placeholder="All sources"
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Campaign</label>
                                <select className="form-control" value={campaign || ""}
                                    disabled={source.length !== 1}
                                    onChange={(e) => this.setState({ campaign: e.target.value })}>
                                    <option value="">Campaign</option>
                                    {campaignOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Service Type</label>
                                <select
                                    className="form-control"
                                    value={service_type}
                                    onChange={(e) => this.setState({ service_type: e.target.value })}
                                >
                                    <option value="">All Service Types</option>
                                    {serviceTypeOptions.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Medicine Type</label>
                                <Select
                                    isMulti
                                    isClearable
                                    isSearchable
                                    options={medicineTypeOptions.map((m) => ({ label: m, value: m }))}
                                    value={medicine_type}
                                    onChange={(selected) => this.setState({ medicine_type: selected || [] })}
                                    placeholder="Select medicine types..."
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Service For</label>
                                <Select
                                    isMulti
                                    isClearable
                                    isSearchable
                                    options={serviceForOptions.map((s) => ({ label: s, value: s }))}
                                    value={service_for}
                                    onChange={(selected) => this.setState({ service_for: selected || [] })}
                                    placeholder="Select services..."
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Selling Purpose</label>
                                <select
                                    className="form-control"
                                    value={selling_purpose}
                                    onChange={(e) => this.setState({ selling_purpose: e.target.value })}
                                >
                                    <option value="">All Selling Purposes</option>
                                    {sellingPurposeOptions.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Location</label>
                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions={false}
                                    isMulti
                                    isClearable
                                    loadOptions={this.loadLocations}
                                    value={location}
                                    onChange={(selected) => this.setState({ location: selected || [] })}
                                    placeholder="Search locations..."
                                />
                            </div>
                        </div>

                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Executive</label>
                                <select
                                    className="form-control"
                                    value={user_id}
                                    onChange={(e) => this.setState({ user_id: e.target.value })}
                                >
                                    <option value="">All Executives</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Team</label>
                                <select
                                    className="form-control"
                                    value={team_id}
                                    onChange={(e) => this.setState({ team_id: e.target.value })}
                                >
                                    <option value="">All Teams</option>
                                    {teams.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>Buy %</label>
                                <div style={{ display: "flex", gap: 5 }}>
                                    <select
                                        className="form-control"
                                        style={{ width: 70, flex: "0 0 auto" }}
                                        value={buy_percentage_operator}
                                        onChange={(e) => this.setState({ buy_percentage_operator: e.target.value })}
                                    >
                                        <option value="">--</option>
                                        <option value=">">&gt;</option>
                                        <option value="<">&lt;</option>
                                        <option value="=">=</option>
                                    </select>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        className="form-control"
                                        style={{ flex: "1 1 auto" }}
                                        placeholder="e.g. 30"
                                        value={buy_percentage_value}
                                        onChange={(e) => this.setState({ buy_percentage_value: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <div className="form-group">
                                <label>&nbsp;</label>
                                <div>
                                    <button type="submit" className="btn btn-primary" style={{ marginRight: 5 }}>
                                        <i className="fas fa-search"></i> Search
                                    </button>
                                    <button type="button" className="btn btn-default" onClick={this.handleReset}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                {/* Summary Cards */}
                {loading ? (
                    <p>
                        <i className="fas fa-spinner fa-spin"></i> Loading...
                    </p>
                ) : (
                    <div>
                        {/* Overview Cards */}
                        <div className="row" style={{ marginBottom: 30 }}>
                            <div className="col-md-6">
                                <div
                                    className="panel panel-success"
                                    style={{ cursor: "pointer", transition: "transform 0.2s" }}
                                    onClick={() => this.navigateToReport("posted")}
                                    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                                >
                                    <div className="panel-heading">
                                        <div className="row">
                                            <div className="col-xs-3">
                                                <i className="fas fa-check-circle fa-5x" style={{ color: "#5cb85c" }}></i>
                                            </div>
                                            <div className="col-xs-9 text-right">
                                                <div style={{ fontSize: 32, fontWeight: "bold", color: "#5cb85c" }}>
                                                    {postedCount}
                                                </div>
                                                <div style={{ fontSize: 14, color: "#666" }}>Posted Leads</div>
                                                <div style={{ fontSize: 13, color: "#3c763d", marginTop: 6 }}>
                                                    Target: <strong>{totalPostedTarget}</strong>
                                                    {" "}| Avg Buy Count: <strong>{avgBuyCount}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="panel-footer" style={{ backgroundColor: "#f4f4f4", textAlign: "center", cursor: "pointer" }}>
                                        <span style={{ color: "#5cb85c", fontWeight: "bold" }}>
                                            View Details <i className="fas fa-arrow-right"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div
                                    className="panel panel-warning"
                                    style={{ cursor: "pointer", transition: "transform 0.2s" }}
                                    onClick={() => this.navigateToReport("attempted")}
                                    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                                >
                                    <div className="panel-heading">
                                        <div className="row">
                                            <div className="col-xs-3">
                                                <i className="fas fa-phone-alt fa-5x" style={{ color: "#f0ad4e" }}></i>
                                            </div>
                                            <div className="col-xs-9 text-right">
                                                <div style={{ fontSize: 32, fontWeight: "bold", color: "#f0ad4e" }}>
                                                    {attemptedCount}
                                                </div>
                                                <div style={{ fontSize: 14, color: "#666" }}>Attempted Leads</div>
                                                <div style={{ fontSize: 13, color: "#8a6d3b", marginTop: 6 }}>
                                                    Target: <strong>{totalAttemptedTarget}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="panel-footer" style={{ backgroundColor: "#f4f4f4", textAlign: "center", cursor: "pointer" }}>
                                        <span style={{ color: "#f0ad4e", fontWeight: "bold" }}>
                                            View Details <i className="fas fa-arrow-right"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Per Day Posting */}
                        <div style={{ marginTop: 30 }}>
                            <div
                                style={{
                                    alignItems: "center",
                                    cursor: "pointer",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    marginBottom: 10,
                                }}
                                onClick={() => this.setState((prevState) => ({ showPerDayPosting: !prevState.showPerDayPosting }))}
                            >
                                <h3 style={{ margin: 0 }}>
                                    <i className="fas fa-calendar-alt"></i> Per Day Posting
                                </h3>
                                <button
                                    type="button"
                                    className="btn btn-default btn-sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        this.setState((prevState) => ({ showPerDayPosting: !prevState.showPerDayPosting }));
                                    }}
                                >
                                    {showPerDayPosting ? "Hide" : "Show"} {" "}
                                    <i className={`fas fa-chevron-${showPerDayPosting ? "up" : "down"}`}></i>
                                </button>
                            </div>

                            {showPerDayPosting && (
                                <div>
                                    <p className="text-muted" style={{ marginBottom: 15 }}>
                                        Daily posting counts for the selected filters. Tele Posted uses status 9 and Auto Posted uses status 6.
                                    </p>
                                    <div className="table-responsive">
                                        <table className="table table-striped table-bordered table-hover">
                                            <thead>
                                                <tr style={{ backgroundColor: "#f5f5f5" }}>
                                                    <th>Date</th>
                                                    <th style={{ textAlign: "center" }}>Tele Posted</th>
                                                    <th style={{ textAlign: "center" }}>Auto Posted</th>
                                                    <th style={{ textAlign: "center" }}>Total Posted</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {perDayPosting.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="4" className="text-center">
                                                            No posting data available
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    perDayPosting.map((day) => (
                                                        <tr key={day.date}>
                                                            <td><strong>{formatDisplayDate(day.date)}</strong></td>
                                                            <td style={{ textAlign: "center" }}>{Number(day.tele_posted) || 0}</td>
                                                            <td style={{ textAlign: "center" }}>{Number(day.direct_posted) || 0}</td>
                                                            <td style={{ textAlign: "center" }}>
                                                                <strong>{Number(day.total_posted) || 0}</strong>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Executive Summary Table */}
                        <div style={{ marginTop: 30 }}>
                            <h3>
                                <i className="fas fa-users"></i> Executive Summary
                            </h3>
                            {isMultiDayRange && (
                                <p className="text-muted" style={{ marginBottom: 15 }}>
                                    Showing total targets for the selected date range. Current daily targets are listed below each total.
                                </p>
                            )}
                            <div className="table-responsive">
                                <table className="table table-striped table-bordered table-hover">
                                    <thead>
                                        <tr style={{ backgroundColor: "#f5f5f5" }}>
                                            <th>Executive Name</th>
                                            <th style={{ textAlign: "center" }}>Posted Leads</th>
                                            <th style={{ textAlign: "center" }}>Total Posted Target</th>
                                            <th style={{ textAlign: "center" }}>Posted %</th>
                                            <th style={{ textAlign: "center" }}>Attempted Leads</th>
                                            <th style={{ textAlign: "center" }}>Total Attempted Target</th>
                                            <th style={{ textAlign: "center" }}>Attempted %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {executiveSummary.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center">
                                                    No data available
                                                </td>
                                            </tr>
                                        ) : (
                                            executiveSummary.map((exec, i) => {
                                                const postedCountValue = Number(exec.posted) || 0;
                                                const attemptedCountValue = Number(exec.attempted) || 0;
                                                const dailyPostedTarget = Number(exec.posted_target) || 0;
                                                const dailyAttemptedTarget = Number(exec.attempted_target) || 0;
                                                const postedTargetValue = isMultiDayRange
                                                    ? Number(exec.total_posted_target) || 0
                                                    : dailyPostedTarget;
                                                const attemptedTargetValue = isMultiDayRange
                                                    ? Number(exec.total_attempted_target) || 0
                                                    : dailyAttemptedTarget;
                                                const postedPct = postedTargetValue
                                                    ? Math.round((postedCountValue / postedTargetValue) * 100)
                                                    : 0;
                                                const attemptedPct = attemptedTargetValue
                                                    ? Math.round((attemptedCountValue / attemptedTargetValue) * 100)
                                                    : 0;
                                                return (
                                                    <tr key={i}>
                                                        <td>
                                                            <strong>{exec.name}</strong>
                                                        </td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <span
                                                                className="label label-success"
                                                                style={{ cursor: "pointer", fontSize: 12 }}
                                                                onClick={() =>
                                                                    this.navigateToReport("posted", exec.user_id)
                                                                }
                                                            >
                                                                {postedCountValue}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <strong>{postedTargetValue || "-"}</strong>
                                                            {isMultiDayRange && (
                                                                <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                                                                    Current daily: {dailyPostedTarget || "-"}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ textAlign: "center" }}>
                                                            {postedTargetValue ? (
                                                                <div>
                                                                    <div className="progress" style={{ marginBottom: 0, height: 18, backgroundColor: "#e8e8e8" }}>
                                                                        <div
                                                                            className={`progress-bar ${postedPct >= 100 ? "progress-bar-success" : postedPct >= 50 ? "progress-bar-warning" : "progress-bar-danger"}`}
                                                                            style={{ width: Math.min(postedPct, 100) + "%", lineHeight: "18px", fontSize: 11 }}
                                                                        >
                                                                            {postedPct}%
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <span
                                                                className="label label-warning"
                                                                style={{ cursor: "pointer", fontSize: 12 }}
                                                                onClick={() =>
                                                                    this.navigateToReport("attempted", exec.user_id)
                                                                }
                                                            >
                                                                {attemptedCountValue}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <strong>{attemptedTargetValue || "-"}</strong>
                                                            {isMultiDayRange && (
                                                                <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                                                                    Current daily: {dailyAttemptedTarget || "-"}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ textAlign: "center" }}>
                                                            {attemptedTargetValue ? (
                                                                <div>
                                                                    <div className="progress" style={{ marginBottom: 0, height: 18, backgroundColor: "#e8e8e8" }}>
                                                                        <div
                                                                            className={`progress-bar ${attemptedPct >= 100 ? "progress-bar-success" : attemptedPct >= 50 ? "progress-bar-warning" : "progress-bar-danger"}`}
                                                                            style={{ width: Math.min(attemptedPct, 100) + "%", lineHeight: "18px", fontSize: 11 }}
                                                                        >
                                                                            {attemptedPct}%
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default AdminLeadsSummary;
